import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  Button,
  Paper,
  Chip,
  FormControlLabel,
  FormGroup,
  Pagination,
  Grid,
  Snackbar,
  Slide,
} from "@mui/material";
import { Refresh, Save } from "@mui/icons-material";
import { FAST_API_BASE_URL } from "constants";
import { extractHubSpotPageContent } from "../utils/hubspot-content-extractor";


const HubSpotDataDisplay = ({ dataType, domainId }) => {
  const [data, setData] = useState([]);
  const [initialSelectedItems, setInitialSelectedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [contentMap, setContentMap] = useState({});
  const [hubspotLastUpdatedAt, setHubspotLastUpdatedAt] = useState({});
  const [autoSyncing, setAutoSyncing] = useState(false);

  // Helper function to normalize timestamps for comparison
  const normalizeTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    try {
      // Handle both ISO format and MySQL datetime format
      let dateObj;
      
      // If it's already in MySQL format (YYYY-MM-DD HH:MM:SS), return as-is
      if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timestamp)) {
        return timestamp;
      }
      
      // Convert ISO string to MySQL datetime format for comparison
      dateObj = new Date(timestamp);
      
      if (isNaN(dateObj.getTime())) return null;
      
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getUTCDate()).padStart(2, '0');
      const hours = String(dateObj.getUTCHours()).padStart(2, '0');
      const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Error normalizing timestamp:', error);
      return null;
    }
  };

  // Check if there are changes from initial selection
  const hasChanges = () => {
    const initialSet = new Set(initialSelectedItems);
    const currentSet = new Set(selectedItems);
    
    if (initialSet.size !== currentSet.size) return true;
    
    for (const item of initialSet) {
      if (!currentSet.has(item)) return true;
    }
    
    return false;
  };

  useEffect(() => {
    if (dataType && domainId) {
      fetchData();
    }
  }, [dataType, domainId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${FAST_API_BASE_URL}/api/hubspot/${dataType}`,
        {
          headers: {
            "DOMAIN-TOKEN": domainId,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        const fetchedData = result[dataType] || [];
        const syncedIds = result.synced_ids || [];
        const lastUpdatedMap = result.hubspot_last_updated_at || {};
        
        setData(fetchedData);
        setInitialSelectedItems(syncedIds);
        setSelectedItems(syncedIds);
        setHubspotLastUpdatedAt(lastUpdatedMap);
        
        // Create content map based on data type
        const newContentMap = {};
        fetchedData.forEach(item => {
          if (item.id) {
            if (dataType === "blogs") {
              // For blogs, use postBody
              newContentMap[item.id] = item.postBody || "";
            } else if (dataType === "pages" || dataType === "landing-pages") {
              // For pages, extract content from layout sections
              try {
                const extractedContent = extractHubSpotPageContent(item);
                newContentMap[item.id] = extractedContent;
              } catch (err) {
                console.error(`Error extracting content for page ${item.id}:`, err);
                newContentMap[item.id] = "";
              }
            }
          }
        });
        setContentMap(newContentMap);

        // Auto-sync updated items
        await autoSyncUpdatedItems(fetchedData, syncedIds, lastUpdatedMap, newContentMap);
      } else {
        setError(result.message || `Failed to fetch ${dataType}`);
      }
    } catch (err) {
      console.error(`Error fetching ${dataType}:`, err);
      setError(`Failed to fetch ${dataType}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const autoSyncUpdatedItems = async (items, syncedIds, lastUpdatedMap, contentMap) => {
    // Find items that need auto-syncing
    const itemsToSync = items.filter(item => {
      // Only check synced items
      if (!syncedIds.includes(item.id)) return false;
      
      const itemUpdated = item.updated || item.updatedAt || item.createdAt;
      const dbUpdated = lastUpdatedMap[item.id];
      
      // If no timestamp in DB, skip
      if (!dbUpdated) return false;
      
      // Normalize both timestamps
      const normalizedItemUpdated = normalizeTimestamp(itemUpdated);
      const normalizedDbUpdated = normalizeTimestamp(dbUpdated);
      
      // Debug logging
      if (normalizedItemUpdated !== normalizedDbUpdated) {
        console.log(`${dataType} ${item.id} needs sync:`, {
          title: item.name || item.htmlTitle,
          hubspotUpdated: itemUpdated,
          normalizedHubspot: normalizedItemUpdated,
          dbUpdated: dbUpdated,
          normalizedDb: normalizedDbUpdated,
          isDifferent: normalizedItemUpdated !== normalizedDbUpdated
        });
      }
      
      // Compare timestamps
      return normalizedItemUpdated && normalizedDbUpdated && 
             normalizedItemUpdated !== normalizedDbUpdated;
    });

    if (itemsToSync.length === 0) return;

    setAutoSyncing(true);
    setSnackbarMessage(`Re-syncing ${itemsToSync.length} updated ${dataType === 'pages' ? 'page' : 'blog'}${itemsToSync.length > 1 ? 's' : ''}...`);
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    let successCount = 0;
    let failCount = 0;

    for (const item of itemsToSync) {
      try {
        const content = contentMap[item.id] || "";
        const webData = {
          web_data: content,
          url: item.url || item.publicUrl || "",
          page_title: item.name || item.htmlTitle || `Untitled ${dataType === 'pages' ? 'Page' : 'Blog'}`,
          page_id: item.id,
          web_content_type: "hubspot",
          hubspot_last_updated_at: item.updated || item.updatedAt || item.createdAt || null
        };

        const response = await fetch(
          `${FAST_API_BASE_URL}/api/web-content`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "DOMAIN-TOKEN": domainId,
            },
            body: JSON.stringify(webData),
          }
        );

        const result = await response.json();
        if (result.resultType === "Success") {
          successCount++;
        } else {
          failCount++;
        }

        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Error auto-syncing ${dataType} ${item.id}:`, error);
        failCount++;
      }
    }

    setAutoSyncing(false);

    // Show result
    if (successCount > 0) {
      setSnackbarMessage(
        `Re-synced ${successCount} updated ${dataType === 'pages' ? 'page' : 'blog'}${successCount > 1 ? 's' : ''}` +
        (failCount > 0 ? ` (${failCount} failed)` : '')
      );
      setSnackbarSeverity(failCount > 0 ? "warning" : "success");
      setSnackbarOpen(true);

      // Trigger sync event
      window.dispatchEvent(new CustomEvent('syncStarted'));
    } else if (failCount > 0) {
      setSnackbarMessage(`Failed to auto-sync ${failCount} ${dataType === 'pages' ? 'page' : 'blog'}${failCount > 1 ? 's' : ''}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleToggle = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === data.length) {
      setSelectedItems(initialSelectedItems);
    } else {
      setSelectedItems(data.map((item) => item.id));
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const currentSyncedIds = initialSelectedItems;
      const newSelectedIds = selectedItems.filter(
        (id) => !currentSyncedIds.includes(id)
      );
      const deselectedIds = currentSyncedIds.filter(
        (id) => !selectedItems.includes(id)
      );

      // Handle deselections
      if (deselectedIds.length > 0) {
        const unsyncPayload = {
          type: "page",
          item_ids: deselectedIds.map((id) => ({ id: `hubspot_${id}` })),
        };

        const unsyncResponse = await fetch(
          `${FAST_API_BASE_URL}/api/unsync/content`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "DOMAIN-TOKEN": domainId,
            },
            body: JSON.stringify(unsyncPayload),
          }
        );

        const unsyncResult = await unsyncResponse.json();
        if (unsyncResult.resultType !== "Success") {
          throw new Error(unsyncResult.message || "Failed to unsync items");
        }
      }

      // Handle new selections
      if (newSelectedIds.length > 0) {
        let successfulSyncs = 0;
        let failedSyncs = 0;

        for (const itemId of newSelectedIds) {
          try {
            const item = data.find(i => i.id === itemId);
            if (!item) {
              failedSyncs++;
              continue;
            }

            const content = contentMap[itemId] || "";
            const webData = {
              web_data: content,
              url: item.url || item.publicUrl || "",
              page_title: item.name || item.htmlTitle || `Untitled ${dataType === 'pages' ? 'Page' : 'Blog'}`,
              page_id: itemId,
              web_content_type: "hubspot",
              hubspot_last_updated_at: item.updated || item.updatedAt || item.createdAt || null
            };

            const response = await fetch(
              `${FAST_API_BASE_URL}/api/web-content`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "DOMAIN-TOKEN": domainId,
                },
                body: JSON.stringify(webData),
              }
            );

            const result = await response.json();
            if (result.resultType === "Success") {
              successfulSyncs++;
            } else {
              failedSyncs++;
            }

            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            failedSyncs++;
          }
        }

        let message = "";
        if (deselectedIds.length > 0 && successfulSyncs > 0) {
          message = `${deselectedIds.length} items unsynced and ${successfulSyncs} items synced`;
        } else if (deselectedIds.length > 0) {
          message = `${deselectedIds.length} items unsynced`;
        } else if (successfulSyncs > 0) {
          message = `${successfulSyncs} items sync started and it may take 5 minutes to complete`;
        }
        
        if (failedSyncs > 0) {
          message += ` (${failedSyncs} failed)`;
        }

        setSnackbarMessage(message || "No changes to save.");
        setSnackbarSeverity(failedSyncs > 0 ? "warning" : "success");
        setSnackbarOpen(true);
        
        // Update initial selection after successful save
        setInitialSelectedItems(selectedItems);
        
        setTimeout(() => {
          fetchData();
        }, 3000);
        
        window.dispatchEvent(new CustomEvent('syncStarted'));

      } else if (deselectedIds.length > 0) {
        // Only deselections
        setSnackbarMessage(`${deselectedIds.length} items unsynced`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        // Update initial selection after successful unsync
        setInitialSelectedItems(selectedItems);
        
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        setSnackbarMessage("No changes to save.");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      }

    } catch (err) {
      console.error("Error saving changes:", err);
      setSnackbarMessage(err.message || "Failed to save changes.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const getTitle = (item) => {
    switch (dataType) {
      case "contacts":
        const { firstname, lastname, email } = item.properties || {};
        return `${firstname || ""} ${lastname || ""}`.trim() || email || "No Name";
      case "blogs":
      case "pages":
        return item.name || item.htmlTitle || `Untitled ${dataType === 'pages' ? 'Page' : 'Blog'}`;
      case "landing-pages":
        return item.name || item.htmlTitle || `Untitled ${dataType === 'pages' ? 'Page' : 'Blog'}`;
      case "emails":
        return item.name || item.subject || "Untitled Campaign";
      default:
        return "Unknown";
    }
  };

  const getUrl = (item) => {
    switch (dataType) {
      case "blogs":
      case "pages":
        return item.url || item.publicUrl || "";
      case "landing-pages":
      return item.url || item.publicUrl || "";
      case "emails":
        return item.contentUrl || "";
      case "contacts":
        return item.portalId && item.id 
          ? `https://app.hubspot.com/contacts/${item.portalId}/contact/${item.id}`
          : "";
      default:
        return "";
    }
  };

  const getDisplayLabel = () => {
    switch (dataType) {
      case "contacts":
        return "Contacts";
      case "blogs":
        return "Blog Posts";
      case "pages":
        return "Pages";
      case "landing-pages":
        return "Landing Pages";
      case "emails":
        return "Email Campaigns";
      default:
        return dataType.charAt(0).toUpperCase() + dataType.slice(1);
    }
  };

  const startIdx = (page - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const paginatedItems = data.slice(startIdx, endIdx);

  if (loading && !autoSyncing) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress sx={{ color: "#219297" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: "8px" }}
        action={
          <IconButton size="small" onClick={fetchData}>
            <Refresh fontSize="small" />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            backgroundColor: snackbarSeverity === "success" ? "#219297" : undefined,
            color: "#fff",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e0f0f0",
          backgroundColor: "#ffffff",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            pb: 2,
            borderBottom: "1px solid #e0f0f0",
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: "#333", fontWeight: 600, mb: 0.5 }}>
              {getDisplayLabel()}
              {autoSyncing && (
                <CircularProgress 
                  size={16} 
                  sx={{ color: "#219297", ml: 2 }} 
                />
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Select items to sync
            </Typography>
          </Box>
          
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={fetchData}
              size="small"
              disabled={autoSyncing}
              sx={{
                color: "#219297",
                "&:hover": {
                  backgroundColor: "#f0f9fb",
                },
              }}
              title="Refresh"
            >
              <Refresh fontSize="small" />
            </IconButton>
            
            <Box sx={{ minWidth: 120 }}>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                style={{
                  width: "100%",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #e0f0f0",
                  backgroundColor: "#ffffff",
                  fontSize: "14px",
                  color: "#333",
                  outline: "none",
                }}
              >
                <option value={1}>1 per page</option>
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={30}>30 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </Box>
          </Box>
        </Box>

        {data.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 3,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No {getDisplayLabel().toLowerCase()} found in your HubSpot account
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                onClick={handleSelectAll}
                sx={{
                  textTransform: "none",
                  color: "#219297",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#f0f9fb",
                  },
                }}
              >
                {selectedItems.length === data.length ? "Deselect All" : "Select All"}
              </Button>
            </Box>

            <Grid container spacing={2}>
              {paginatedItems.map((item) => {
                const itemId = item.id;
                const isSelected = selectedItems.includes(itemId);
                const title = getTitle(item);
                const url = getUrl(item);

                return (
                  <Grid item xs={12} key={itemId}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "8px",
                        border: "1px solid",
                        borderColor: isSelected ? "#219297" : "#e0f0f0",
                        backgroundColor: isSelected ? "#f0f9fb" : "#ffffff",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#219297",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggle(itemId)}
                          sx={{
                            color: "#999",
                            "&.Mui-checked": {
                              color: "#219297",
                            },
                            mt: -0.5,
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#333",
                              fontSize: "0.95rem",
                              mb: 0.5,
                            }}
                          >
                            {title}
                          </Typography>
                          {url && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#219297",
                                fontSize: "0.85rem",
                                wordBreak: "break-all",
                              }}
                            >
                              {url}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
            
            {data.length > itemsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={Math.ceil(data.length / itemsPerPage)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  shape="rounded"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#666',
                      fontSize: '0.875rem',
                    },
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: '#219297',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#1a767a',
                      },
                    },
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mt: 3,
                pt: 2,
                borderTop: "1px solid #e0f0f0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <Save />
                    )
                  }
                  onClick={handleSave}
                  disabled={saving || !hasChanges() || autoSyncing}
                  sx={{
                    backgroundColor: hasChanges() ? "#219297" : "#e0f0f0",
                    color: hasChanges() ? "#ffffff" : "#999",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderRadius: "6px",
                    px: 3,
                    py: 1,
                    "&:hover": {
                      backgroundColor: hasChanges() ? "#1a767a" : "#e0f0f0",
                    },
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </>
  );
};

export default HubSpotDataDisplay;